<template>
  <div class="board-game-detail-page container mt-4">
    <div v-if="loading" class="text-center">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-else-if="boardGame" class="board-game-details">
      <div class="row">
        <div class="col-12">
          <div class="card mb-4 game-header-image-container">
            <img 
              :src="boardGame.headerImageUrl || getGameImage(boardGame.type)" 
              class="card-img-top" 
              :alt="`${boardGame.name} board game`"
              style="max-height: 300px; object-fit: cover;"
            >
            <div v-if="isOwner" class="image-upload-overlay">
              <input 
                type="file" 
                ref="headerImageInput"
                @change="uploadHeaderImage"
                accept="image/*"
                class="d-none"
              >
              <div class="d-flex gap-2">
                <button 
                  class="btn btn-primary btn-sm" 
                  @click="triggerImageUpload"
                >
                  <i class="fas fa-upload me-1"></i> Upload Header Image
                </button>
                <button 
                  class="btn btn-secondary btn-sm" 
                  data-bs-toggle="modal" 
                  data-bs-target="#aiImageGeneratorModal"
                >
                  <i class="fas fa-magic me-1"></i> Generate AI Image
                </button>
              </div>
            </div>
            <div class="card-body">
              <h1 class="card-title mb-3">{{ boardGame.name }}</h1>
            </div>
          </div>
        </div>
      </div>

      <!-- AI Image Generator Modal -->
      <!-- Teleported to <body> deliberately. theme.css sets
           `#app > * { position: relative; z-index: 1 }` to lift page content above
           the paper texture, which makes every routed page a stacking context. A
           modal left inside it is capped at that z-index, while Bootstrap appends
           .modal-backdrop to <body> at z-index 1050 — so the backdrop painted OVER
           the dialog and the screen just went dark with nothing to click. -->
      <Teleport to="body">
        <div class="modal fade" id="aiImageGeneratorModal" tabindex="-1" aria-labelledby="aiImageGeneratorModalLabel" aria-hidden="true">
          <div class="modal-dialog modal-lg">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title" id="aiImageGeneratorModalLabel">Generate AI Header Image</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <div class="mb-3">
                  <label for="aiImagePrompt" class="form-label">Image Prompt</label>
                  <!-- A useful image prompt is two or three sentences, and a
                       single-line input hid all but the last few words of it. -->
                  <textarea
                    id="aiImagePrompt"
                    class="form-control"
                    v-model="aiImagePrompt"
                    rows="4"
                    maxlength="1000"
                    placeholder="Describe the image you want. Mention the subject, the mood, the setting and the art style — for example: a weathered trading schooner at anchor in a volcanic archipelago at dusk, hand-inked linework, muted teal and amber."
                  ></textarea>
                  <div class="form-text">
                    {{ aiImagePrompt.length }} / 1000 ·
                    <span v-if="boardGame && (boardGame.theme || boardGame.cardStyle)">
                      Your game's art direction is applied automatically — just
                      describe the subject.
                    </span>
                    <span v-else>
                      Set a theme on the game and it will be applied
                      automatically to every image.
                    </span>
                  </div>
                </div>
                <div class="text-center mb-3" v-if="aiImageGenerating">
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden">Generating...</span>
                  </div>
                  <p>Generating AI images...</p>
                </div>
                <div v-else-if="aiImageResults.length > 0" class="row g-3">
                  <div 
                    v-for="(imageUrl, index) in aiImageResults" 
                    :key="index" 
                    class="col-4"
                  >
                    <div 
                      class="ai-image-option" 
                      :class="{ 'selected': selectedAIImage === imageUrl }"
                      @click="selectedAIImage = imageUrl"
                    >
                      <img 
                        :src="imageUrl" 
                        class="img-fluid rounded" 
                        :alt="`AI Generated Image ${index + 1}`"
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button 
                  type="button" 
                  class="btn btn-secondary" 
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  class="btn btn-primary" 
                  @click="generateAIImages"
                  :disabled="!aiImagePrompt.trim()"
                >
                  Generate Images
                </button>
                <button 
                  type="button" 
                  class="btn btn-success" 
                  @click="selectAIImage(selectedAIImage)"
                  :disabled="!selectedAIImage"
                  data-bs-dismiss="modal"
                >
                  Use Selected Image
                </button>
              </div>
            </div>
          </div>
        </div>
      </Teleport>

      <div class="row">
        <div class="col-md-8">
          <div class="card mb-4">
            <div class="card-body">
              <h5 class="card-title">Description</h5>
              <p class="card-text">{{ boardGame.description }}</p>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-body">
              <h5 class="card-title">Game Details</h5>
              <ul class="list-group list-group-flush">
                <li class="list-group-item">
                  <strong>Game Type:</strong> 
                  {{ formatGameType(boardGame.type) }}
                </li>
                <li class="list-group-item">
                  <strong>Minimum Players:</strong> 
                  {{ boardGame.minPlayers }}
                </li>
                <li class="list-group-item">
                  <strong>Complexity:</strong> 
                  {{ formatComplexity(boardGame.complexity) }}
                </li>
                <li class="list-group-item">
                  <strong>Created:</strong> 
                  {{ formatDate(boardGame.createdAt) }}
                </li>
                <li class="list-group-item">
                  <strong>Card Style:</strong> 
                  {{ boardGame.cardStyle }}
                </li>
              </ul>
            </div>
          </div>

          <!-- Rules Section -->
          <div class="card mb-4">
            <!-- On a phone four buttons cannot share a row with the title.
                 The title takes the full width and the actions scroll
                 horizontally underneath rather than stacking. -->
            <div class="card-header section-head">
              <h5 class="card-title mb-0">Game Rules</h5>
              <div class="section-actions">
                <button 
                  v-if="isOwner && !isEditingRules" 
                  class="btn btn-sm btn-outline-primary me-2" 
                  @click="startEditingRules"
                >
                  Update Rules
                </button>
                <button 
                  v-if="boardGame.rules"
                  class="btn btn-sm btn-outline-success me-2" 
                  @click="downloadRules"
                  :disabled="isDownloadingRules"
                >
                  <span v-if="isDownloadingRules" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  Download Rules
                </button>
                <router-link
                  v-if="isOwner && boardGame.rules"
                  :to="{ name: 'PlaytestReview', query: { gameId: boardGame.id, mode: 'quick-read' } }"
                  class="btn btn-sm btn-outline-warning me-2"
                >
                  <i class="ra ra-book me-1"></i>AI Playtest
                </router-link>
                <button
                  class="btn btn-sm btn-outline-secondary"
                  @click="toggleRulesVisibility"
                >
                  {{ showRules ? 'Hide' : 'Show' }} Rules
                </button>
              </div>
            </div>
            <div v-if="showRules" class="card-body rules-section">
              <div v-if="isEditingRules" class="rules-editor">
                <div class="rules-editor-container">
                  <textarea 
                    ref="rulesEditor"
                    v-model="editedRules"
                    class="form-control mb-2"
                    rows="10"
                    placeholder="Write your game rules using markdown..."
                  ></textarea>
                </div>
                <div class="rules-editor-actions d-flex justify-content-end mt-2">
                  <button 
                    class="btn btn-secondary me-2" 
                    @click="cancelEditRules"
                  >
                    Cancel
                  </button>
                  <button 
                    class="btn btn-primary" 
                    @click="saveRules"
                  >
                    Save Rules
                  </button>
                </div>
              </div>
              <div v-else-if="boardGame.rules" class="rules-preview">
                <div v-html="renderedRules" class="markdown-content"></div>
              </div>
              <div v-else class="text-muted">
                No rules have been added yet.
                <span v-if="isOwner">
                  Click "Update Rules" to add some.
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <!-- Quick Links -->
        <div class="card mb-4">
            <div class="card-body">
              <h5 class="card-title">Assets</h5>
              <div class="d-grid gap-2">
                <router-link 
                  :to="{ 
                    name: 'BuildCards', 
                    query: { 
                      boardGameId: boardGame.id, 
                      boardGameName: boardGame.name 
                    } 
                  }" 
                  class="btn btn-primary"
                >
                  View Game Cards
                </router-link>
              </div>
              <p class="text-muted mt-2 small">
                Manage and design cards for this board game.
              </p>
              <div class="d-grid gap-2">
                <router-link
                  :to="{
                    name: 'Decks',
                    params: { gameId: boardGame.id },
                    query: $route.query.share ? { share: $route.query.share } : {}
                  }"
                  class="btn btn-outline-primary"
                >
                  Decks
                </router-link>
              </div>
              <p class="text-muted mt-2 small">
                Download the cards for each version, in colour or black and white.
              </p>
                <div class="d-grid gap-2">
                  <router-link 
                    :to="{ 
                      name: 'BuildBoards', 
                      params:{
                        boardGameId: boardGame.id
                      }
                    }" 
                    class="btn btn-primary"
                  >
                    View Boards
                  </router-link>
                </div>
                <p class="text-muted mt-2 small">
                  Design and manage game boards.
                </p>
              </div>
          </div>
          

          <GameAidsPanel
            :game-id="boardGame.id"
            :can-edit="isOwner || isEditor"
            @changed="onAssetsChanged"
          />

          <GameVersionPanel
            ref="versionPanel"
            :game-id="boardGame.id"
            :can-edit="isOwner || isEditor"
          />

          <div class="card mb-4">
            <div class="card-body">
              <h5 class="card-title">Actions</h5>
              <div class="d-grid gap-2 mb-3">
                <button 
                  v-if="user && user.uid !== boardGame.userId"
                  class="btn"
                  :class="isFollowing ? 'btn-secondary' : 'btn-outline-primary'"
                  @click="toggleFollow"
                  :disabled="isTogglingFollow"
                >
                  <span v-if="isTogglingFollow" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  <i :class="isFollowing ? 'fas fa-user-minus' : 'fas fa-user-plus'" class="me-1"></i>
                  {{ isFollowing ? 'Unfollow' : 'Follow' }}
                </button>
                <button 
                  class="btn btn-primary" 
                  @click="editBoardGame"
                  :disabled="!isOwner && !isEditor"
                >
                  Edit Board Game
                </button>
                <button 
                  class="btn btn-danger" 
                  @click="deleteBoardGame"
                  :disabled="!isOwner"
                >
                  Delete Board Game
                </button>
                
                <!-- Sharing lives in one dialog, the way people already
                     expect it to: people and their roles, then general
                     access, then a link. The inline invite box that used to
                     be here could only ever make somebody a full editor. -->
                <button
                  v-if="canManageAccess"
                  class="btn btn-outline-primary mt-3"
                  @click="showShare = true"
                >
                  Share
                </button>
                <p v-if="canManageAccess" class="text-muted small mt-2 mb-0">
                  {{ accessSummary }}
                </p>

                <!-- Accept Invite Button (for users with pending invites) -->
                <button 
                  v-if="hasPendingInvite" 
                  class="btn btn-success mt-2" 
                  @click="acceptEditorInvite"
                  :disabled="isAcceptingInvite"
                >
                  <span v-if="isAcceptingInvite" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Accept Editor Invite
                </button>
              </div>
              
              <!-- Permissions message -->
              <p v-if="!isOwner && !isEditor && !hasPendingInvite" class="text-muted mt-2 small">
                Only the creator and editors can edit this board game.
              </p>
              
              <!-- Editors and Owner List -->
              <div v-if="isOwner || isEditor" class="mt-3">
                <h6>Owners & Editors</h6>
                <ul class="list-group list-group-flush">
                  <!-- Owner -->
                  <li class="list-group-item px-0 py-1 d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                      <div class="user-icon bg-primary text-white me-2">
                        <i class="fas fa-user"></i>
                      </div>
                      <div>
                        <span class="badge bg-primary me-2">Owner</span>
                        <span class="user-name">{{ ownerName || 'Unknown' }}</span>
                        <div class="small text-muted">{{ ownerEmail || 'No email available' }}</div>
                      </div>
                    </div>
                  </li>
                  
                  <!-- Editors -->
                  <li 
                    v-for="editor in editors" 
                    :key="editor.id" 
                    class="list-group-item px-0 py-1 d-flex justify-content-between align-items-center"
                  >
                    <div class="d-flex align-items-center">
                      <div 
                        class="user-icon me-2 text-white"
                        :class="{
                          'bg-success': editor.status === 'accepted',
                          'bg-warning': editor.status === 'pending',
                          'bg-danger': editor.status === 'rejected'
                        }"
                      >
                        <i class="fas fa-user-edit"></i>
                      </div>
                      <div>
                        <div>
                          <span 
                            class="badge me-2"
                            :class="{
                              'bg-success': editor.status === 'accepted',
                              'bg-warning text-dark': editor.status === 'pending',
                              'bg-danger': editor.status === 'rejected'
                            }"
                          >
                            {{ editor.status.charAt(0).toUpperCase() + editor.status.slice(1) }}
                          </span>
                          <span class="user-name">{{ editor.name || 'Editor' }}</span>
                        </div>
                        <div class="small text-muted">{{ editor.email }}</div>
                      </div>
                    </div>
                    <div v-if="isOwner && editor.status === 'pending'">
                      <button 
                        class="btn btn-sm btn-outline-danger" 
                        @click="cancelEditorInvite(editor.id)"
                        :disabled="isCancellingInvite"
                      >
                        Cancel
                      </button>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Board Game Modal -->
    <!-- Also teleported: this overlay carries its own dimming so it was not
         blacked out, but at z-index 1000 inside the page's stacking context it
         rendered UNDER the navbar (1030). -->
    <Teleport to="body">
      <div v-if="isEditingBoardGame" class="edit-board-game-overlay">
        <div class="edit-board-game-container">
          <CreateBoardGamePage 
            ref="createBoardGameComponent"
            @close="isEditingBoardGame = false"
            :isEditing="true"
            :boardGameId="boardGame.id"
          />
        </div>
      </div>
    </Teleport>
  </div>

    <ShareDialog
      v-if="showShare"
      :game-id="boardGame.id"
      :game-name="boardGame.name"
      :owner-label="currentUser ? currentUser.email : 'You'"
      @close="showShare = false"
      @changed="access = $event"
    />
</template>

<style scoped>
/* --- section headers (mobile) --------------------------------------- */
.section-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.section-actions {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

@media (max-width: 767.98px) {
  .section-head .card-title { width: 100%; font-size: 1rem; }
  /* These used to scroll horizontally with the scrollbar hidden, which put
     "Show Rules" off the right edge of an iPhone with nothing to indicate it
     existed. Four small buttons wrap to two rows perfectly well, and nothing
     ends up hidden. */
  .section-actions {
    width: 100%;
    flex-wrap: wrap;
  }
  .section-actions .btn {
    white-space: nowrap;
    flex: 0 0 auto;
    font-size: 0.78rem;
    padding: 0.25rem 0.55rem;
    /* The original markup spaces these with me-2; the flex gap does it now. */
    margin-right: 0 !important;
  }
}

.edit-board-game-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.edit-board-game-container {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.user-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 14px;
}

.user-name {
  font-weight: 500;
}
</style>

<script>
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc, deleteDoc, updateDoc, Timestamp } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import GameAidsPanel from '@/components/GameAidsPanel.vue'
import GameVersionPanel from '@/components/GameVersionPanel.vue'
import { firestore, storage, generateGameImage, getEditors, updateEditorStatus, followBoardGame, isFollowingBoardGame } from '@/firebase'
import { sparkforgeApi } from '@/sparkforgeApi'
import ShareDialog from './components/ShareDialog.vue'
import EasyMDE from 'easymde'
import { marked } from 'marked'
import CreateBoardGamePage from './CreateBoardGamePage.vue'

export default {
  name: 'BoardGameDetailPage',
  components: {
    CreateBoardGamePage,
    GameAidsPanel,
    GameVersionPanel,
    ShareDialog
  },
  data() {
    return {
      boardGame: null,
      loading: true,
      error: null,
      currentUser: null,
      // Open by default: the rules ARE the game, and the toggle that revealed
      // them was the last button in a horizontally-scrolling strip on mobile,
      // so people reported not knowing the rules were there at all.
      showRules: true,
      isEditingRules: false,
      editedRules: '',
      rulesEditor: null,
      isUploadingImage: false,
      isDownloadingRules: false,
      aiImagePrompt: '',
      aiImageGenerating: false,
      aiImageResults: [],
      selectedAIImage: null,
      isEditingBoardGame: false,
      // Editor invitation
      showShare: false,
      access: null,
      isFollowing: false,
      isTogglingFollow: false,
      editors: [],
      isEditor: false,
      hasPendingInvite: false,
      pendingInviteId: null,
      isAcceptingInvite: false,
      isCancellingInvite: false,
      ownerEmail: '',
      ownerName: ''
    }
  },
  computed: {
    /** Only the owner may change access, so only the owner sees Share. */
    canManageAccess () {
      return this.isOwner
    },
    /**
     * One line under the button so the current state is visible without
     * opening the dialog — the thing people actually want to know is "who can
     * see this right now".
     */
    accessSummary () {
      if (!this.access) return 'Loading who has access…'
      const n = (this.access.collaborators || []).length
      const people = n === 0 ? 'no one else' : n === 1 ? '1 other person' : `${n} other people`
      const general = {
        private: 'Restricted',
        playtest: 'Anyone with the link',
        public: 'Public'
      }[this.access.visibility] || 'Restricted'
      return `${general} · shared with ${people}`
    },
    user() {
      return this.$store.state.user;
    },
    isOwner() {
      return this.currentUser && 
             this.boardGame && 
             this.currentUser.uid === this.boardGame.userId
    },
    renderedRules() {
      return this.boardGame && this.boardGame.rules 
        ? marked.parse(this.boardGame.rules) 
        : ''
    }
  },
  methods: {
    /** The one-line access summary under the Share button. Owner only. */
    async loadAccess () {
      if (!this.isOwner || !this.boardGame) return
      try {
        this.access = await sparkforgeApi.getAccess(this.boardGame.id)
      } catch (e) {
        // Not fatal — the button still opens the dialog, which will show the
        // real error if there is one.
        console.warn('Could not load access', e)
      }
    },
    onAssetsChanged() {
      if (this.$refs.versionPanel) this.$refs.versionPanel.refresh();
    },

    formatGameType(type) {
      return type.charAt(0).toUpperCase() + type.slice(1)
    },
    formatComplexity(complexity) {
      return complexity.charAt(0).toUpperCase() + complexity.slice(1)
    },
    formatDate(timestamp) {
      if (!(timestamp instanceof Timestamp)) {
        return 'Unknown date'
      }
      return timestamp.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    getGameImage(type) {
      // Default game image
      const defaultImage = 'https://storage.googleapis.com/rpg-framework-a73be.appspot.com/CastleIntro.jpg'
      
      // In the future, you can add specific images for different game types
      const gameTypeImages = {
        strategy: defaultImage,
        party: defaultImage,
        cooperative: defaultImage,
        card: defaultImage,
        board: defaultImage,
        other: defaultImage
      }
      
      return gameTypeImages[type] || defaultImage
    },
    triggerImageUpload() {
      // Trigger hidden file input
      this.$refs.headerImageInput.click()
    },
    async uploadHeaderImage(event) {
      // Check if user is owner
      if (!this.isOwner) {
        alert('You do not have permission to upload images')
        return
      }

      // Get the file
      const file = event.target.files[0]
      
      // Validate file
      if (!file) return
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File is too large. Maximum size is 5MB.')
        return
      }

      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      if (!validTypes.includes(file.type)) {
        alert('Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP).')
        return
      }

      try {
        // Show loading state
        this.isUploadingImage = true

        // Create a reference to the storage location
        const imageRef = storageRef(
          storage, 
          `board-game-images/${this.boardGame.id}/${Date.now()}_${file.name}`
        )

        // Upload the file
        const snapshot = await uploadBytes(imageRef, file)

        // Get the download URL
        const downloadURL = await getDownloadURL(snapshot.ref)

        // Update the board game document with the new image URL
        const boardGameRef = doc(firestore, 'boardGames', this.boardGame.id)
        await updateDoc(boardGameRef, {
          headerImageUrl: downloadURL,
          updatedAt: Timestamp.now()
        })

        // Update local state
        this.boardGame.headerImageUrl = downloadURL

        // Clear the file input
        this.$refs.headerImageInput.value = null
      } catch (error) {
        console.error('Error uploading image:', error)
        alert('Failed to upload image. Please try again.')
      } finally {
        // Hide loading state
        this.isUploadingImage = false
      }
    },
    toggleRulesVisibility() {
      this.showRules = !this.showRules
    },
    startEditingRules() {
      this.isEditingRules = true
      this.editedRules = this.boardGame.rules || ''
      
      // Ensure rules are visible when editing
      this.showRules = true

      // Use nextTick to ensure the textarea is in the DOM
      this.$nextTick(() => {
        // Initialize EasyMDE
        this.rulesEditor = new EasyMDE({
          element: this.$refs.rulesEditor,
          spellChecker: false,
          status: false
        })
      })
    },
    cancelEditRules() {
      this.isEditingRules = false
      
      // Destroy EasyMDE instance
      if (this.rulesEditor) {
        this.rulesEditor.cleanup()
        this.rulesEditor = null
      }
    },
    async downloadRules() {
      if (!this.boardGame.rules) {
        alert('No rules available to download')
        return
      }

      this.isDownloadingRules = true
      try {
        // Routed through the Sparkforge API. This used to POST the rules to
        // `print_instructions` in a DIFFERENT Google Cloud project
        // (career-shepard), unauthenticated — so the game's rules text left
        // this project entirely to reach a function nobody here owns. The API
        // stores the PDF and returns a link, so there is no blob to shuttle.
        const { sparkforgeApi } = await import('@/sparkforgeApi')
        const exported = await sparkforgeApi.exportPdf(this.boardGame.id, 'rules')
        window.open(exported.url, '_blank', 'noopener')
      } catch (error) {
        console.error('Error downloading rules:', error)
        alert('Failed to download rules. Please try again.')
      } finally {
        this.isDownloadingRules = false
      }
    },

    async saveRules() {
      if (!this.isOwner) {
        alert('You do not have permission to edit rules')
        return
      }

      try {
        const boardGameRef = doc(firestore, 'boardGames', this.boardGame.id)
        
        // Update the rules in Firestore
        await updateDoc(boardGameRef, {
          rules: this.editedRules,
          updatedAt: Timestamp.now()
        })

        // Update local state
        this.boardGame.rules = this.editedRules

        // Exit editing mode
        this.isEditingRules = false
        
        // Destroy EasyMDE instance
        if (this.rulesEditor) {
          this.rulesEditor.cleanup()
          this.rulesEditor = null
        }
      } catch (err) {
        console.error('Error saving rules:', err)
        alert('Failed to save rules')
      }
    },
    async fetchBoardGame() {
      const boardGameId = this.$route.params.id
      
      try {
        const auth = getAuth()
        this.currentUser = auth.currentUser

        const boardGameRef = doc(firestore, 'boardGames', boardGameId)
        const boardGameSnap = await getDoc(boardGameRef)

        if (boardGameSnap.exists()) {
          this.boardGame = {
            id: boardGameSnap.id,
            ...boardGameSnap.data()
          }
          
          // Get owner information
          if (this.boardGame.userId) {
            // In a real app, you would fetch the user's details from a users collection
            // For simplicity, we'll use the user ID and add a placeholder name
            this.ownerEmail = this.currentUser && this.currentUser.uid === this.boardGame.userId 
              ? this.currentUser.email || this.boardGame.userId
              : this.boardGame.userId + '@example.com'
            
            this.ownerName = this.currentUser && this.currentUser.uid === this.boardGame.userId
              ? this.currentUser.displayName || 'Game Owner'
              : 'Game Owner'
          }
          
          // Fetch editors
          await this.fetchEditors()
          // And the access summary, so the Share button can say what the
          // current state is without being opened.
          await this.loadAccess()
          
          // Check if current user is an editor
          if (this.currentUser) {
            this.checkEditorStatus()
            
            // Check if user has a pending invite
            await this.checkPendingInvite()
          }
        } else {
          this.error = 'Board game not found'
        }
      } catch (err) {
        console.error('Error fetching board game:', err)
        this.error = err.message || 'Failed to load board game'
      } finally {
        this.loading = false
      }
    },
    
    async fetchEditors() {
      try {
        this.editors = await getEditors(this.boardGame.id)
      } catch (err) {
        console.error('Error fetching editors:', err)
      }
    },

    async checkFollowStatus() {
      if (!this.currentUser || !this.boardGame) return;
      
      try {
        this.isFollowing = await isFollowingBoardGame(this.currentUser.uid, this.boardGame.id);
      } catch (error) {
        console.error('Error checking follow status:', error);
      }
    },

    async toggleFollow() {
      if (!this.currentUser || !this.boardGame) return;
      
      this.isTogglingFollow = true;
      try {
        const isNowFollowing = await followBoardGame(this.currentUser.uid, this.boardGame.id);
        this.isFollowing = isNowFollowing;
      } catch (error) {
        console.error('Error toggling follow status:', error);
      } finally {
        this.isTogglingFollow = false;
      }
    },
    
    checkEditorStatus() {
      // Check if current user is an editor with accepted status
      if (this.currentUser && this.editors.length > 0) {
        const userEditor = this.editors.find(
          editor => editor.email === this.currentUser.email && editor.status === 'accepted'
        )
        this.isEditor = !!userEditor
      } else {
        this.isEditor = false
      }
    },
    
    async checkPendingInvite() {
      if (!this.currentUser || !this.currentUser.email) return
      
      try {
        // Check if user has a pending invite for this board game
        const pendingEditor = this.editors.find(
          editor => editor.email === this.currentUser.email && 
                   editor.status === 'pending' &&
                   editor.boardGameId === this.boardGame.id
        )
        
        this.hasPendingInvite = !!pendingEditor
        this.pendingInviteId = pendingEditor ? pendingEditor.id : null
      } catch (err) {
        console.error('Error checking pending invites:', err)
      }
    },
    
    async acceptEditorInvite() {
      if (!this.pendingInviteId) return
      
      this.isAcceptingInvite = true
      
      try {
        await updateEditorStatus(this.pendingInviteId, 'accepted')
        
        // Update local state
        this.hasPendingInvite = false
        this.pendingInviteId = null
        this.isEditor = true
        
        // Refresh editors list
        await this.fetchEditors()
      } catch (err) {
        console.error('Error accepting invite:', err)
      } finally {
        this.isAcceptingInvite = false
      }
    },
    
    async cancelEditorInvite(editorId) {
      if (!editorId) return
      
      this.isCancellingInvite = true
      
      try {
        await updateEditorStatus(editorId, 'rejected')
        
        // Refresh editors list
        await this.fetchEditors()
      } catch (err) {
        console.error('Error cancelling invite:', err)
      } finally {
        this.isCancellingInvite = false
      }
    },
    editBoardGame() {
      this.isEditingBoardGame = !this.isEditingBoardGame
      
      // If opening the edit form, pre-populate with current board game details
      if (this.isEditingBoardGame && this.boardGame) {
        this.$nextTick(() => {
          const createBoardGameComponent = this.$refs.createBoardGameComponent
          if (createBoardGameComponent) {
            createBoardGameComponent.boardGame = {
              name: this.boardGame.name,
              description: this.boardGame.description,
              type: this.boardGame.type,
              minPlayers: this.boardGame.minPlayers,
              complexity: this.boardGame.complexity
            }
          }
        })
      }
    },
    async deleteBoardGame() {
      if (!this.isOwner) {
        alert('You do not have permission to delete this board game')
        return
      }

      const confirmDelete = confirm('Are you sure you want to delete this board game?')
      
      if (confirmDelete) {
        try {
          const boardGameRef = doc(firestore, 'boardGames', this.boardGame.id)
          await deleteDoc(boardGameRef)
          
          // Redirect to home page after deletion
          this.$router.push({
            name: 'Home',
            query: { 
              message: `Board game "${this.boardGame.name}" has been deleted.`
            }
          })
        } catch (err) {
          console.error('Error deleting board game:', err)
          alert('Failed to delete board game')
        }
      }
    },
    async generateAIImages() {
      // Validate prompt
      const trimmedPrompt = this.aiImagePrompt.trim()
      if (!trimmedPrompt) {
        alert('Please enter a prompt for image generation')
        return
      }

      // Validate ownership
      if (!this.isOwner) {
        alert('You do not have permission to generate images')
        return
      }

      try {
        // Set generating state
        this.aiImageGenerating = true
        this.aiImageResults = []
        this.selectedAIImage = null

        // NOTE: the raw prompt is what gets sent. There used to be a
        // `fullPrompt` here wrapping it in "Board game header image for a game
        // about: …", built and then never passed to anything. Leaving the
        // behaviour as it is rather than silently changing what every
        // generation asks for.
        // A page banner, not card art. The style also carries the game's own
        // saved art direction server-side, so the prompt box only needs the
        // subject.
        const options = {
          style: 'header',
          aspect_ratio: '16:9',
          gameId: this.boardGame.id
        }
        const imageUrls = await generateGameImage(this.aiImagePrompt, options);

        // This used to branch on `imageDataUrls`, a variable whose declaration
        // was commented out. `typeof` on an undeclared name does not throw, so
        // the branch was simply always false and the else ran every time —
        // dead code that read as though base64 results were still handled.
        this.aiImageResults = imageUrls || []
      } catch (error) {
        console.error('Error generating AI images:', error)
        
        // Provide more specific error feedback
        const errorMessage = error.message.includes('API key') 
          ? 'Invalid API key. Please check your configuration.' 
          : 'Failed to generate AI images. Please try again or check your prompt.'
        
        alert(errorMessage)
        
        // Fallback to a default placeholder image
        this.aiImageResults = [
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACklEQVR4nGMAAQAABQABDQottAAAAABJRU5ErkJggg=='
        ]
      } finally {
        // Reset generating state
        this.aiImageGenerating = false
      }
    },
    async selectAIImage(imageUrl) {
      // Validate ownership
      if (!this.isOwner) {
        alert('You do not have permission to select images')
        return
      }

      try {
        // The API already copied the bytes into our storage and handed back a
        // permanent URL, so just point the game at it. The previous version
        // re-downloaded the image with fetch() and re-uploaded it — a
        // cross-origin request to the storage bucket, which has no CORS
        // configuration, so it threw every time and the save always failed.
        const boardGameRef = doc(firestore, 'boardGames', this.boardGame.id)
        await updateDoc(boardGameRef, {
          headerImageUrl: imageUrl,
          updatedAt: Timestamp.now()
        })
        this.boardGame.headerImageUrl = imageUrl

        // Reset AI image state
        this.aiImageResults = []
        this.selectedAIImage = null
        this.aiImagePrompt = ''
      } catch (error) {
        console.error('Error selecting AI image:', error)
        alert('Failed to select AI image. Please try again.')
      }
    }
  },
  created() {
    this.fetchBoardGame();
    
    // Set up auth state listener
    const auth = getAuth();
    onAuthStateChanged(auth, async (user) => {
      this.currentUser = user;
      if (user && this.boardGame) {
        await this.checkFollowStatus();
      }
    });
  },
  mounted() {
    // Configure marked to allow HTML (for safety, you might want to sanitize)
    marked.setOptions({
      sanitize: true,
      breaks: true,
      gfm: true
    })
  },
  beforeUnmount() {
    // Cleanup EasyMDE if it exists
    if (this.rulesEditor) {
      this.rulesEditor.cleanup()
    }
  }
}
</script>

<style scoped>
.board-game-details {
  max-width: 1200px;
  margin: 0 auto;
}

.game-header-image-container {
  position: relative;
}

.image-upload-overlay {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.game-header-image-container:hover .image-upload-overlay {
  opacity: 1;
}

.rules-section {
  max-height: 600px;
  overflow-y: auto;
}

.rules-editor-container {
  position: relative;
  max-height: 500px;
}

.rules-editor textarea {
  resize: vertical;
  min-height: 300px;
  max-height: 500px;
}

.markdown-content {
  line-height: 1.6;
  max-height: 500px;
  overflow-y: auto;
}

.markdown-content h1 { font-size: 1.5rem; }
.markdown-content h2 { font-size: 1.3rem; }
.markdown-content h3 { font-size: 1.2rem; }
.markdown-content ul, .markdown-content ol {
  padding-left: 20px;
  margin-bottom: 15px;
}
.markdown-content pre {
  background-color: #f4f4f4;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
}

.rules-editor-actions {
  position: sticky;
  bottom: 0;
  background-color: white;
  padding-top: 10px;
  border-top: 1px solid #e9ecef;
}

.ai-image-option {
  cursor: pointer;
  border: 3px solid transparent;
  transition: border-color 0.3s ease;
}

.ai-image-option:hover {
  border-color: #007bff;
}

.ai-image-option.selected {
  border-color: #28a745;
  box-shadow: 0 0 10px rgba(40, 167, 69, 0.5);
}
</style>